import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import Expand from '../../components/Navigation/Expand';
import { useContext, useState } from 'react';
import deleteSvg from '../../assets/delete-white.svg';
import { useIframeContext } from '../../context/IframeContext';
import CompaniesContext from '../../context/CompaniesContext';

function DeleteCompany() {
    const { isIframe } = useIframeContext();
    const { fetchCompanies } = useContext(CompaniesContext);

    const [showConfirmation, setShowConfirmation] = useState(true);
    const [deleteStatus, setDeleteStatus] = useState('');
    const [showImage, setShowImage] = useState(false);
    const [showContinueButton, setShowContinueButton] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const { rowData } = location.state;
    const keyMetrics = rowData.total_interactions > 0;

    // Handler for navigating back to Initiatives page
    const navigateBack = () => {
        navigate(-1);
    };

    const handleContinue = () => {
        navigate('/companies');
    }

    const handleNext = async (id: number | string | undefined) => {
        console.log('key metrics present:', 'interactions -', rowData.total_interactions);

        setError(null);

        if (keyMetrics) {
            console.log('Cannot delete initiative that contains key metrics.')
            setShowConfirmation(false);
            setDeleteStatus('To delete this initiative, you must first move or remove any associated interactions.');
            return;
        }

        if (!id) {
            console.error('No ID provided for deletion');
            return;
        }

        try {
            const response = await fetch(`${import.meta.env.VITE_COMMERCE_BACKEND_URL}/api/deletecompany?companyId=${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
    
            if (!response.ok) {
                const responseData = await response.json();
                throw new Error(responseData.error || 'Failed to delete company');
            }
            console.log(`Company with ID ${id} successfully deleted`);
            await fetchCompanies(); // ✅ Refresh company list after delete
            setDeleteStatus('Company deleted successfully');
            setShowImage(true);
            setShowConfirmation(false);
            setShowContinueButton(true);
        } catch (error) {
            // Log the caught error to Sentry
            Sentry.captureException(error);
            console.error('Error sending data to backend:', error);
            setError('There was an issue deleting the company. Please try again later.');
        }
    };
    

    return (
        <div className="m-2 p-4">
            {/* Expand button */}
            {isIframe && 
                <div className='flex justify-end'>
                    <div className="flex ml-auto mb-4">
                        <Expand/>
                    </div>
                </div>
            }
            

            {/* Delete Company */}
            <div className="mb-8 border border-solid border-color-tableGray rounded-md">
                <div className='border-b border-solid border-titanium-white mb-5 bg-gotham-gray rounded-t-md'>
                    <h2 className="text-white font-semibold py-3 ml-7">Delete Company</h2>
                </div>
                <div className='flex ml-4 sm:ml-8 h-20 items-center'>
                    {showImage && 
                    <img src={deleteSvg} alt="Deleted Successfully" className="p-2 mr-4 h-9 w-10 border border-red-500 bg-red-500 rounded-full" />}
                    <p className='font-semibold'>{showConfirmation ? 'Are you sure that you want to permanently delete this company?' : deleteStatus}</p>
                </div>
                {/* Display the server error message if it exists */}
                {error && (
                    <div className="text-red-500 text-sm ml-8">
                        {error}
                    </div>
                )}
                <div className='border-t border-solid border-color-tableGray mt-5 mb-2'>
                    {!showImage && showConfirmation && (
                        <button
                            id="next"
                            onClick={() => handleNext(id)}
                            className="ml-7 mt-5 mb-3 w-36 h-9 text-center text-white bg-gotham-gray border border-solid border-gotham-gray rounded-md focus:outline-none focus:border-blue-400  hover:bg-blue-400 hover:border-blue-500"
                        > Next
                        </button>
                    )}
                    {!showImage && (
                        <button
                            id="cancel"
                            onClick={navigateBack}
                            className="ml-7 mt-5 mb-3 w-36 h-9 text-center text-gotham-gray bg-white border border-solid border-gotham-gray rounded-md focus:outline-none focus:border-red-500  hover:bg-red-400 hover:border-red-500 hover:text-white"
                        > Cancel
                        </button>
                    )}
                    {showContinueButton && (
                        <button
                            id="continue"
                            onClick={handleContinue}
                            className="ml-7 mt-5 mb-3 w-36 h-9 text-center text-white bg-gotham-gray border border-solid border-gotham-gray rounded-md focus:outline-none focus:border-blue-400  hover:bg-blue-400 hover:border-blue-500"
                        > Continue
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DeleteCompany;